export class TimeUnit {
  static name = 'TimeUnit';

  constructor(nameLong, nameShort, nameExtraShort, scale) {
    this.nameLong = nameLong;
    this.nameShort = nameShort;
    this.nameExtraShort = nameExtraShort;
    this.scale = scale;
  }

  isSameAs(that) {
    if (this.nameLong !== that.nameLong) return false;
    if (this.nameShort !== that.nameShort) return false;
    if (this.nameExtraShort !== that.nameExtraShort) return false;
    if (this.scale !== that.scale) return false;
    return true;
  }

  toString() {
    return this.nameShort;
  }

  convertValueTo(value, timeUnitTo) {
    if (!Object.values(TimeUnit.units).includes(timeUnitTo)) return undefined;
    return value * this.scale / timeUnitTo.scale;
  }

  static convert(value, timeUnitFrom, timeUnitTo) {
    if (!Object.values(this.units).includes(timeUnitFrom)) return undefined;
    if (!Object.values(this.units).includes(timeUnitTo)) return undefined;
    return value * timeUnitFrom.scale / timeUnitTo.scale;
  }
}

Object.defineProperty(
  TimeUnit,
  'units',
  {
    value: {
      msec: new TimeUnit('millisecond', 'msec', 'ms', 1),
      sec:  new TimeUnit('second',      'sec',  's',  1 * 1000),
      min:  new TimeUnit('minute',      'min',  'm',  1 * 1000 * 60),
      hour: new TimeUnit('hour',        'hour', 'h',  1 * 1000 * 60 * 60),
      day:  new TimeUnit('day',         'day',  'd',  1 * 1000 * 60 * 60 * 24),
    },
    writable: false,
  },
);

export class TimeValue {
  static name = 'TimeValue';

  static InvalidTimeUnitError = class extends Error {
    static name = 'InvalidTimeUnitError';
  }
    
  constructor(value = 0, timeUnit = TimeUnit.units.msec) {
    if (!Object.values(TimeUnit.units).includes(timeUnit)) {
      throw new TimeValue.InvalidTimeUnitError('Invalid time unit.')
    }
    this.value = value;
    this.timeUnit = timeUnit;
  }

  clone() {
    return new TimeValue(this.value, this.timeUnit);
  }

  isSameAs(that) {
    if (this.value !== that.value) return false;
    if (!this.timeUnit.isSameAs(that.timeUnit)) return false;
    return true;
  }

  valueOf() {
    return this.msec;
  }

  toString() {
    return `${this.value} [${this.timeUnit}]`;
  }

  getTimeComponents() {
    function msecToHour(msec)           { return Math.trunc(msec / TimeUnit.units.hour.scale) }
    function hourToMsec(hour)           { return hour * TimeUnit.units.hour.scale }
    function msecToMin(msec)            { return Math.trunc(msec / TimeUnit.units.min.scale) }
    function minToMsec(min)             { return min * TimeUnit.units.min.scale }
    function msecToSec(msec)            { return Math.trunc(msec / TimeUnit.units.sec.scale) }
    function secToMsec(sec)             { return sec * TimeUnit.units.sec.scale }

    let remainingMsec = this.msec;
    let hour = msecToHour(remainingMsec);
    remainingMsec -= hourToMsec(hour);
    let min = msecToMin(remainingMsec);
    remainingMsec -= minToMsec(min);
    let sec = msecToSec(remainingMsec);
    remainingMsec -= secToMsec(sec);
    let msec = remainingMsec;
    return { hour, min, sec, msec };
  }

  getTimeComponentsAsString() {
    function padZero(number, numDigits) { return ('0'.repeat(numDigits) + String(number)).slice(-numDigits) }

    let { hour, min, sec, msec } = this.getTimeComponents();
    return { hour: String(hour), min: padZero(min, 2), sec: padZero(sec, 2), msec: padZero(msec, 3) };
  }

  toFormatString() {
    let { hour, min, sec, msec } = this.getTimeComponentsAsString();
    return `${hour}:${min}:${sec}.${msec}`;
  }

  convertValueTo(timeUnitTo) {
    if (this.timeUnit === timeUnitTo) return this.value;
    return this.timeUnit.convertValueTo(this.value, timeUnitTo);
  }

  getOffsetFrom(that) { return this.subtract(that) }

  assign(that) { this.value = that.convertValueTo(this.timeUnit); return this }
  ceil(timeUnit = this.timeUnit) { return new this.constructor(Math.ceil(this.convertValueTo(timeUnit)), timeUnit) }
  floor(timeUnit = this.timeUnit) { return new this.constructor(Math.floor(this.convertValueTo(timeUnit)), timeUnit) }
  round(timeUnit = this.timeUnit) { return new this.constructor(Math.round(this.convertValueTo(timeUnit)), timeUnit) }

  clampTo(min, max) { return (this.isLessThan(min))? min : ((this.isGreaterThan(max))? max : this) }
  floorTo(that) { return (this.isLessThan(that))? that : this }
  ceilTo(that) { return (this.isGreaterThan(that))? that : this }

  addSelf(that) { this.value += that.convertValueTo(this.timeUnit); return this }
  subtractSelf(that) { this.value -= that.convertValueTo(this.timeUnit); return this }
  multiplySelf(factor) { this.value *= factor; return this }
  divideSelf(factor) { this.multiplySelf(1 / factor); return this }
  absoluteSelf() { this.value = Math.abs(this.value); return this }

  add(that) { return new this.constructor(this.value + that.convertValueTo(this.timeUnit), this.timeUnit) }
  subtract(that) { return new this.constructor(this.value - that.convertValueTo(this.timeUnit), this.timeUnit) }
  multiply(factor) { return new this.constructor(this.value * factor, this.timeUnit) }
  divide(factor) { return this.multiply(1 / factor) }
  absolute() { return new this.constructor(Math.abs(this.value), this.timeUnit) }
  get half() { return this.divide(2) }

  isEqualTo(that) { return this.value === that.convertValueTo(this.timeUnit) }
  isLessThan(that) { return this.value < that.convertValueTo(this.timeUnit) }
  isLessThanOrEqualTo(that) { return this.value <= that.convertValueTo(this.timeUnit) }
  isGreaterThan(that) { return this.value > that.convertValueTo(this.timeUnit) }
  isGreaterThanOrEqualTo(that) { return this.value >= that.convertValueTo(this.timeUnit) }

  static get zero() { return TimeValue.msec(0) }

  get msec() { return this.convertValueTo(TimeUnit.units.msec) }
  set msec(value) { this.assign(TimeValue.msec(value)) }
  static msec(value) { return new this(value, TimeUnit.units.msec) }

  get sec() { return this.convertValueTo(TimeUnit.units.sec) }
  set sec(value) { this.assign(TimeValue.sec(value)) }
  static sec(value) { return new this(value, TimeUnit.units.sec) }

  get min() { return this.convertValueTo(TimeUnit.units.min) }
  set min(value) { this.assign(TimeValue.min(value)) }
  static min(value) { return new this(value, TimeUnit.units.min) }

  get hour() { return this.convertValueTo(TimeUnit.units.hour) }
  set hour(value) { this.assign(TimeValue.hour(value)) }
  static hour(value) { return new this(value, TimeUnit.units.hour) }

  get day() { return this.convertValueTo(TimeUnit.units.day) }
  set day(value) { this.assign(TimeValue.day(value)) }
  static day(value) { return new this(value, TimeUnit.units.day) }
}

export default TimeValue;
